import { Product } from "@/api/product-api";
import { isDiscontinued } from "@/components/tactsuit/PriceDisplay";
import { ProductUtil } from "@/utils/ProductUtil";

/**
 * 품절 CTA 에 **`Find a retailer` 를 그리는가** (#5293, 시안 29444:145342).
 *
 * ★훅·JSX 안의 조건식이 아니라 **순수함수로** 둔다. 조건 자체를 단위테스트로 잠글 수
 * 있어야 한다 — 페이지에 인라인으로 두면 세 항 중 무엇을 지워도 아무 테스트도 안 깨진다
 * (실측: 이 함수로 빼기 전, 단종 게이트와 `retailers` 조건을 각각 제거한 뮤턴트가 둘 다
 * 통과했다). 같은 이유로 `CartService.hasGameKeySkuOption`(#5348)도 순수함수다.
 *
 * 세 항의 근거가 각각 다르다:
 *
 * 1. **품절일 때만.** 재고가 있으면 `Add to Bag`·`Checkout` 이 그 자리를 쓴다.
 * 2. **단종은 제외.** 단종 제품은 리테일러에도 재고가 없어 헛걸음이 된다. 판정 축이
 *    재고와 **다르다** — `isDiscontinued` 는 코드 하드코딩 slug 목록이고
 *    `isProductSoldOut` 는 재고 기반이다. ⚠️ 유일한 단종 제품(`tactosy-for-feet`)이
 *    `retailers` 를 **3곳 갖고 있어서**, 이 게이트가 없으면 데이터만 보고 버튼이 뜬다.
 * 3. **`retailers` 가 있어야.** prod 36개 중 **29개가 빈 배열**이라 그쪽이 다수다
 *    (변형 `*-camo` · 게임키 번들 · 글러브 · 액세서리). 라인 베이스에서 물려받지
 *    **않는다** — 색상·`?bundle=` 변경으로 아이콘이 사라졌다 나타나는 것은 정상 동작이다
 *    (`displayProduct` 가 바뀌므로). 2026-08-25 기획 확인 완료.
 */
export function shouldShowRetailerCta(product: Product): boolean {
  return (
    ProductUtil.isProductSoldOut(product) &&
    !isDiscontinued(product.slug) &&
    product.retailers.length > 0
  );
}

/**
 * 재고가 있을 때의 **원형 아이콘 버튼**을 그리는가 (시안 29116:30970 맨 왼쪽 40px).
 *
 * 품절 버전과 대상은 같고(리테일러가 등록된 제품) **모양만** 다르다 — 재고가 있으면
 * `Add to Bag`·`Checkout` 이 라벨 자리를 쓰므로 리테일러는 아이콘으로 줄어든다.
 *
 * 단종을 따로 빼지 않는다: 단종 제품은 정의상 품절이라 이 술어에 도달하지 않는다
 * (실측: 유일한 단종 제품 `tactosy-for-feet` 은 재고 0). 조건을 늘리면 "왜 있는지"
 * 모르는 항이 하나 생긴다.
 */
export function shouldShowRetailerIcon(product: Product): boolean {
  return !ProductUtil.isProductSoldOut(product) && product.retailers.length > 0;
}
